/**
 * Create-Workspace page — PR H.
 *
 * Reachable from the Dashboard's `+ Workspace` button (top of the
 * workspaces section) and from the chat-header dashboard icon when
 * the user has zero workspaces.
 *
 * One-field form (name); native Telegram MainButton drives submit.
 * The BackButton (PR 1) navigates back to `/dashboard`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../api/client";
import { createWorkspace } from "../api/workspaces";
import { useBackButton } from "../hooks/useBackButton";
import { haptic } from "../hooks/useHaptic";
import { useMainButton } from "../hooks/useMainButton";

export function CreateWorkspace(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useBackButton(useCallback(() => navigate("/dashboard"), [navigate]));

  const [name, setName] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (n: string) => createWorkspace(n),
    onSuccess: async () => {
      haptic.notify("success");
      // Invalidate everything that lists workspaces — Dashboard +
      // chat header + the bot's `/workspaces` will all see the new
      // row on their next fetch.
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      navigate("/dashboard");
    },
    onError: () => haptic.notify("error"),
  });

  const canSubmit = name.trim().length > 0 && !mutation.isPending;

  const submit = useCallback((): void => {
    setValidationError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setValidationError("Workspace name is required.");
      return;
    }
    mutation.mutate(trimmed);
  }, [mutation, name]);

  useMainButton(
    useMemo(
      () => ({
        text: mutation.isPending ? "Creating…" : "Create workspace",
        onClick: submit,
        loading: mutation.isPending,
        disabled: !canSubmit,
      }),
      [canSubmit, mutation.isPending, submit],
    ),
  );

  const serverError =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error instanceof Error
        ? mutation.error.message
        : null;

  return (
    <div
      className="min-h-dvh"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
    >
      <header
        className="sticky top-0 z-10 px-3 py-2.5"
        style={{
          background: "var(--color-bg)",
          borderBottom: "1px solid var(--surface-hairline)",
        }}
      >
        <h1 className="text-[16px] font-semibold">New workspace</h1>
        <p className="text-[11px] mt-0.5" style={{ color: "var(--color-hint)" }}>
          A workspace groups projects for one team or company.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="px-4 py-3 space-y-3 pb-24"
        noValidate
      >
        <label className="block">
          <div
            className="text-[11px] uppercase tracking-wide mb-1"
            style={{ color: "var(--color-hint)" }}
          >
            Name *
          </div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Inc."
            maxLength={200}
            required
            className="w-full rounded-lg px-3 py-2 text-sm outline-none"
            style={{
              background: "var(--color-secondary-bg)",
              color: "var(--color-text)",
              border: "1px solid transparent",
            }}
          />
          {validationError ? (
            <p
              className="text-xs mt-1"
              style={{ color: "var(--color-destructive)" }}
              role="alert"
            >
              {validationError}
            </p>
          ) : null}
        </label>

        {serverError ? (
          <p
            className="text-sm"
            style={{ color: "var(--color-destructive)" }}
            role="alert"
          >
            {serverError}
          </p>
        ) : null}

        {/* Hidden submit — lets Enter in the input fire the form
            without us wiring keyboard shortcuts. The native
            MainButton is the primary CTA. */}
        <button type="submit" hidden disabled={!canSubmit} aria-hidden="true" />
      </form>
    </div>
  );
}
